import { FormattedNumber } from '@/components/dashboard/shared';
import { LongDate } from '@/components/dateFormats';
import { Card } from '@/components/ui/largeCard';
import { DownloadResultFragment } from '@/gql/graphql';
import { DynamicLink } from '@/reactRouterPlugins';
import { DownloadTitle } from '@/routes/occurrence/download/key/downloadKey';
import { DownloadFilterSummary } from '@/routes/occurrence/download/key/sections/queryCard';
import { fragmentManager } from '@/services/fragmentManager';
import { formatBytes } from '@/utils/formatBytes';
import { FormattedMessage } from 'react-intl';

fragmentManager.register(/* GraphQL */ `
  fragment DownloadResult on Download {
    key
    created
    modified
    doi
    downloadLink
    status
    totalRecords
    numberDatasets
    size
    license
    request {
      predicate
      sql: sqlFormatted
      format
      type
      description
      gbifMachineDescription
      checklistKey
    }
  }
`);

interface DownloadResultProps {
  download: DownloadResultFragment;
  onCancel?: (key: string) => void;
  hideStatus?: boolean;
}

export function DownloadResult({ download, hideStatus }: DownloadResultProps) {
  const { size, unit } = formatBytes(download?.size ?? 0, 0);

  const pageId = download.request?.type === 'OCCURRENCE' ? 'downloadKey' : 'eventDownloadKey';
  return (
    <DynamicLink pageId={pageId} variables={{ key: download.key }}>
      <Card className="g-mb-4 hover:g-shadow-lg g-transition-shadow g-duration-300 hover:g-border-primary-300">
        <article className="">
          <div className="g-p-4 g-flex g-flex-col g-gap-2">
            <div className="g-flex g-justify-between g-items-start">
              <div className="g-flex-grow">
                <div className="g-flex g-items-center g-gap-2">
                  <h3 className="g-text-base g-font-semibold">
                    <DownloadTitle download={download} ignoreStatus={hideStatus} />
                  </h3>
                </div>

                {download.request?.description && (
                  <p className="g-text-sm g-text-slate-700 g-mt-2">
                    {download.request.description}
                  </p>
                )}
              </div>
            </div>

            <div className="g-flex g-flex-wrap g-gap-4 g-text-sm g-text-slate-600">
              <div>
                <FormattedMessage id="downloadKey.created" />:{' '}
                <span className="g-font-semibold">
                  {download.created ? (
                    <LongDate value={download?.created} />
                  ) : (
                    <FormattedMessage id="phrases.unknownDate" />
                  )}
                </span>
              </div>
              {download.size && (
                <div>
                  <FormattedMessage id="downloadKey.fileSize" />:{' '}
                  <span className="g-font-semibold">
                    <FormattedNumber value={size} /> {unit}
                  </span>
                </div>
              )}
              {download.request?.format && (
                <div>
                  <FormattedMessage id="downloadKey.format" />:{' '}
                  <span className="g-font-semibold">
                    <FormattedMessage id={`enums.downloadFormat.${download?.request?.format}`} />
                  </span>
                </div>
              )}
              {download.numberDatasets && (
                <div>
                  <span className="g-font-semibold">
                    <FormattedMessage
                      id="counts.nDatasets"
                      values={{ total: download.numberDatasets }}
                    />
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="g-border-t g-border-gray-200 g-p-4 g-overflow-auto">
            <DownloadFilterSummary download={download} restrictHeight="g-max-h-80" />
          </div>
        </article>
      </Card>
    </DynamicLink>
  );
}
